
import React, { useState, useEffect, useCallback } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Paper, 
    Grid, 
    CircularProgress, 
    Container, 
    Chip, 
    Divider, 
    Stack, 
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import DnsIcon from '@mui/icons-material/Dns';
import SecurityIcon from '@mui/icons-material/Security';
import PublicIcon from '@mui/icons-material/Public';
import GroupIcon from '@mui/icons-material/Group';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StorageIcon from '@mui/icons-material/Storage';
import LanIcon from '@mui/icons-material/Lan';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { useWebSocket, useUser } from './WebSocketContext';
import { useTranslation } from 'react-i18next';

const PurchaseCredits = () => {
    const { t } = useTranslation();
    const { sendMessage, subscribeToMessages, isConnected } = useWebSocket();
    const user = useUser();
    const [loadingBundle, setLoadingBundle] = useState(null);
    const [tokenPurchasable, setTokenPurchasable] = useState(true);
    const [refreshRequested, setRefreshRequested] = useState(false);
    const [pendingRefresh, setPendingRefresh] = useState(false);
    const [currentTier, setCurrentTier] = useState(user?.userInfo?.tier || '');
    const theme = useTheme();

    const clearPaymentParams = useCallback(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('payment_result')) {
            params.delete('payment_result');
            params.delete('payment_reason');
            params.delete('session_id');
            const newQuery = params.toString();
            const newUrl = `${window.location.pathname}${newQuery ? `?${newQuery}` : ''}${window.location.hash}`;
            window.history.replaceState({}, document.title, newUrl);
        }
    }, []);

    const handleCloseDialog = useCallback(() => {
        setDialogOpen(false);
        clearPaymentParams();
    }, [clearPaymentParams]);

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogStatus, setDialogStatus] = useState('success'); // 'success', 'error', 'cancel'
    const [dialogMessage, setDialogMessage] = useState('');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const result = params.get('payment_result');
        if (!result) {
            return;
        }

        const reason = params.get('payment_reason');
        let message = '';
        let status = 'success';

        if (result === 'success') {
            message = t('purchase.success', 'Payment succeeded; credits have been added.');
            status = 'success';
            if (!refreshRequested) {
                setPendingRefresh(true);
            }
        } else if (result === 'cancel') {
            message = reason
                ? t('purchase.cancel_reason', 'Payment failed: {{reason}}', { reason })
                : t('purchase.cancelled', 'Payment was cancelled.');
            status = 'cancel';
        } else {
            message = t('purchase.unexpected_status', 'Payment status: {{status}}', { status: result });
            status = 'error';
        }

        setDialogMessage(message);
        setDialogStatus(status);
        setDialogOpen(true);
    }, [t, refreshRequested]);

    useEffect(() => {
        if (pendingRefresh && isConnected && !refreshRequested) {
            sendMessage({ command: 'refresh_credits' });
            setRefreshRequested(true);
            setPendingRefresh(false);
        }
    }, [pendingRefresh, isConnected, refreshRequested, sendMessage]);

    useEffect(() => {
        const unsubscribe = subscribeToMessages(['refresh_credits'], (message) => {
            setPendingRefresh(false);
            const data = message.data || {};
            if (data.status === 'success') {
                setDialogMessage(t('purchase.credits_updated', 'Credits have been refreshed.'));
            } else {
                setDialogStatus('error');
                setDialogMessage(data.message || t('purchase.refresh_failed', 'Unable to sync credits. Please try again.'));
            }
        });

        return () => {
            unsubscribe();
        };
    }, [subscribeToMessages, t, handleCloseDialog]);

    useEffect(() => {
        const unsubscribe = subscribeToMessages(['configuration_data'], (message) => {
            const tierValue = message.data?.tier;
            const tokenPurchasableValue = message.data?.tokenPurchasable;
            console.log('PurchaseCredits configuration_data tier:', tierValue, 'full message:', message);
            if (tierValue !== undefined && tierValue !== null) {
                setCurrentTier(String(tierValue).trim());
            }
            if (tokenPurchasableValue !== undefined && tokenPurchasableValue !== null) {
                setTokenPurchasable(Boolean(tokenPurchasableValue));
            }
        });

        return () => {
            unsubscribe();
        };
    }, [subscribeToMessages]);

    const commonFeatures = [
        { text: t('purchase.feature_dedicated_instance', 'Dedicated Private Instance'), icon: <DnsIcon fontSize="small" /> },
        { text: t('purchase.feature_isolated_db', 'Isolated Secure Database'), icon: <SecurityIcon fontSize="small" /> },
        { text: t('purchase.feature_custom_portal', 'Custom Domain & Portal'), icon: <PublicIcon fontSize="small" /> },
        { text: t('purchase.feature_unlimited_users', 'Unlimited User Accounts'), icon: <GroupIcon fontSize="small" /> }
    ];

    const parseTierNumber = (value) => {
        if (value === undefined || value === null) return null;
        const digits = String(value).replace(/[^0-9]/g, '');
        const parsed = Number(digits);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    };

    const currentTierNumber = parseTierNumber(currentTier);

    const bundles = [
        { id: 'bundle_5k', credits: 5000, price: '$10', color: theme.palette.success.main, type: 'LITE', tier: 'Tier 1', tierValue: 1, storage: '30 GB', features: commonFeatures },
        { id: 'bundle_20k', credits: 20000, price: '$25', color: theme.palette.primary.main, recommended: true, type: 'PRO', tier: 'Tier 2', tierValue: 2, storage: '30 GB', features: commonFeatures },
        { id: 'bundle_100k', credits: 150000, price: '$100', color: theme.palette.secondary.main, type: 'BUSINESS', tier: 'Tier 3', tierValue: 3, storage: '30 GB', features: commonFeatures, prioritySupport: true },
        { 
            id: 'bundle_enterprise', 
            credits: 0, 
            price: t('purchase.custom_price', 'Custom'), 
            color: theme.palette.text.primary, 
            type: 'ENTERPRISE', 
            isContact: true, 
            storage: t('purchase.custom_storage', 'Custom'),
            features: [
                ...commonFeatures,
                { text: t('purchase.feature_on_prem', 'On-Premises Deployment'), icon: <HomeWorkIcon fontSize="small" /> },
                { text: t('purchase.feature_local_integration', 'Local Infrastructure & Storage Integration'), icon: <LanIcon fontSize="small" /> }
            ],
            prioritySupport: true
        },
    ];

    // --- Subscription / monthly-reset helpers ---
    const parseSubscriptionDate = () => {
        // Look for common subscription fields returned in userInfo (backend may add these later)
        const raw = user?.userInfo?.subscription_start_date || user?.userInfo?.subscription?.start_date || user?.userInfo?.subscription_anniversary || null;
        if (!raw) return null;
        const d = (typeof raw === 'number') ? new Date(raw) : new Date(String(raw));
        return Number.isFinite(d.getTime()) ? d : null;
    };

    const getNextResetDate = (startDate) => {
        if (!startDate) return null;
        const now = new Date();
        const day = startDate.getUTCDate(); // use day-of-month as anniversary

        // target month/year: start with current month
        let year = now.getUTCFullYear();
        let month = now.getUTCMonth();

        // If today is after (or equal to) this month's anniversary, move to next month
        // If today is on/after this month's anniversary, advance to next month
        if (now.getUTCDate() >= day) {
            month += 1;
            if (month > 11) {
                month = 0;
                year += 1;
            }
        }

        // Use the last valid day for target month if day doesn't exist (e.g., Feb 30)
        const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
        const targetDay = Math.min(day, lastDay);
        const result = new Date(Date.UTC(year, month, targetDay));
        return result;
    };

    const subscriptionStart = parseSubscriptionDate();
    const nextReset = getNextResetDate(subscriptionStart);

    const formatDate = (d) => {
        if (!d) return '';
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const buildReturnUrl = (status, reasonKey = null) => {
        const url = new URL(window.location.href);
        url.searchParams.set('payment_result', status);
        if (status === 'success') {
            url.searchParams.set('session_id', '{CHECKOUT_SESSION_ID}');
            url.searchParams.delete('payment_reason');
        } else if (reasonKey) {
            url.searchParams.set('payment_reason', reasonKey);
            url.searchParams.delete('session_id');
        } else {
            url.searchParams.delete('session_id');
        }
        return url.toString();
    };

    const tierDisplay = currentTier ? currentTier : '';
    if (!tokenPurchasable) {
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                    {t('purchase.disabled_title', 'Credit purchases are disabled')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
                    {t('purchase.disabled_description', 'This installation does not expose the pricing page. Contact your administrator if you need credit top-ups enabled.')}
                </Typography>
            </Container>
        );
    }

    const handleBuy = (bundleId) => {
        // set loading state for the clicked bundle
        setLoadingBundle(bundleId);

        // Create a one-time subscription for buy_credits responses
        let didRespond = false;
        const unsubscribe = subscribeToMessages(['buy_credits'], (message) => {
            didRespond = true;
            try {
                const data = message.data || {};
                if (data.status === 'success' && data.url) {
                    window.location.href = data.url;
                } else {
                    setDialogMessage(data.message || t('purchase.initiation_failed', 'Failed to initiate purchase'));
                    setDialogStatus('error');
                    setDialogOpen(true);
                }
            } finally {
                setLoadingBundle(null);
                try { unsubscribe(); } catch (e) { /* ignore */ }
                if (timeoutId) clearTimeout(timeoutId);
            }
        });

        // Fallback timeout to ensure spinner doesn't hang indefinitely
        const timeoutId = setTimeout(() => {
            if (!didRespond) {
                setLoadingBundle(null);
                setDialogMessage(t('purchase.timeout', 'Purchase request timed out. Please try again.'));
                setDialogStatus('error');
                setDialogOpen(true);
                try { unsubscribe(); } catch (e) { /* ignore */ }
            }
        }, 10000); // 10s timeout

        // Send the properly formatted command object
        try {
            sendMessage({
                command: 'buy_credits',
                data: {
                    bundleId,
                    successUrl: buildReturnUrl('success'),
                    cancelUrl: buildReturnUrl('cancel', 'user_cancelled'),
                }
            });
        } catch (e) {
            // If sending fails immediately, clear loading and unsubscribe
            clearTimeout(timeoutId);
            setLoadingBundle(null);
            try { unsubscribe(); } catch (err) { /* ignore */ }
            setDialogMessage(t('purchase.send_failed', 'Failed to send purchase request'));
            setDialogStatus('error');
            setDialogOpen(true);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
                {t('purchase.title', 'Top Up Your Credits')}
            </Typography>

            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 2, maxWidth: 680, mx: 'auto' }}>
                {t('purchase.subtitle', 'Choose a bundle below to add credits to your account. Payments are processed securely.')}
            </Typography>

            {tierDisplay && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                    <Chip
                        label={tierDisplay}
                        size="small"
                        variant="outlined"
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            borderColor: 'rgba(92, 99, 242, 0.4)',
                            color: theme.palette.text.primary
                        }}
                    />
                </Box>
            )}

            {/* Subscription reset info */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: (theme) => theme.palette.background.paper, px: 2, py: 1, borderRadius: 1.5, boxShadow: (theme) => theme.shadows[0] }}>
                    <Typography variant="body2" color="text.secondary">
                        {subscriptionStart ? (
                            <>
                                {t('purchase.credits_reset_on_anniversary', 'Credits reset every month on your subscription anniversary.')}
                                <Box component="span" sx={{ ml: 1, fontWeight: 700 }}>{formatDate(nextReset)}</Box>
                            </>
                        ) : (
                            <>
                                {t('purchase.credits_reset_info_no_sub', 'Credits reset monthly on your subscription anniversary.')}
                                <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                    {t('purchase.subscribe_to_enable', 'Subscribe to enable automatic monthly resets.')}
                                </Box>
                            </>
                        )}
                    </Typography>

                    <Tooltip title={t('purchase.credits_reset_tooltip', 'Credits are replenished on the same day-of-month as your subscription start date. If that day does not exist in a month, the last day of the month is used.')}> 
                        <InfoOutlinedIcon fontSize="small" color="action" sx={{ ml: 0.5 }} />
                    </Tooltip>
                </Box>
            </Box>

            <Grid container spacing={3} justifyContent="center">
                {bundles.map((bundle) => {
                    const isLoading = loadingBundle === bundle.id;
                    const priceNumber = parseFloat(String(bundle.price).replace(/[^0-9.]/g, '')) || 0;
                    const pricePer = priceNumber && bundle.credits ? `$${(priceNumber / bundle.credits).toFixed(4)}` : '';
                    const bundleTierNumber = parseTierNumber(bundle.tierValue ?? bundle.tier);
                    let tierStatus = null;
                    if (bundleTierNumber !== null && currentTierNumber !== null) {
                        if (bundleTierNumber === currentTierNumber) {
                            tierStatus = t('purchase.tier_status.current', 'Current tier');
                        } else if (bundleTierNumber > currentTierNumber) {
                            tierStatus = t('purchase.tier_status.upgrade', 'Upgrade');
                        } else {
                            tierStatus = t('purchase.tier_status.downgrade', 'Downgrade');
                        }
                    }

                    return (
                        <Grid item key={bundle.id} xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                            <Paper
                                elevation={bundle.recommended ? 6 : 1}
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    borderRadius: 2,
                                    position: 'relative',
                                    transition: 'transform 0.18s ease',
                                    width: '100%',
                                    height: '100%',
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    boxShadow: '0 8px 24px rgba(15,23,42,0.05)',
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: '0 10px 28px rgba(15,23,42,0.08)'
                                    },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    alignItems: 'center'
                                }}
                            >
                                {bundle.recommended && (
                                    <Chip
                                        label={t('purchase.recommended', 'Recommended')}
                                        color="primary"
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: -12,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            fontWeight: 700,
                                            boxShadow: '0 6px 16px rgba(87,87,176,0.15)'
                                        }}
                                    />
                                )}

                                {/* header: tier + plan badge */}
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                                    {bundle.tier && (
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {bundle.tier}
                                        </Typography>
                                    )}
                                    <Chip
                                        label={bundle.type}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontWeight: 800, borderColor: 'rgba(0,0,0,0.06)' }}
                                    />
                                    {tierStatus && (
                                        <Chip
                                            label={tierStatus}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                borderColor: 'rgba(100,100,100,0.25)',
                                                backgroundColor: 'rgba(0,0,0,0.04)'
                                            }}
                                        />
                                    )}
                                </Stack>

                                <Box sx={{ 
                                    width: 72, 
                                    height: 72, 
                                    borderRadius: '50%', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    bgcolor: `${bundle.color}10`,
                                    color: bundle.color,
                                    mb: 1
                                }}>
                                    <MonetizationOnIcon sx={{ fontSize: 34 }} />
                                </Box>

                                <Typography variant="h5" component="div" sx={{ fontWeight: 800 }}>
                                    {bundle.isContact ? t('purchase.unlimited_credits', 'Custom') : bundle.credits.toLocaleString()}
                                </Typography>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t('purchase.credits', 'Credits')}
                                </Typography>

                                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, flexGrow: 1, width: '100%', px: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <StorageIcon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.8 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                            {bundle.storage} {t('purchase.secure_storage', 'Secure Storage')}
                                        </Typography>
                                    </Stack>

                                    {bundle.features && bundle.features.map((feature, idx) => (
                                        <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ textAlign: 'left' }}>
                                            <Box sx={{ color: 'text.secondary', opacity: 0.8, display: 'flex' }}>
                                                {feature.icon || <CheckIcon fontSize="small" />}
                                            </Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                                {feature.text}
                                            </Typography>
                                        </Stack>
                                    ))}

                                    {bundle.prioritySupport && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <SupportAgentIcon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.8 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                                {t('purchase.priority_support', '24/7 Priority Support')}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: bundle.color }}>{bundle.price}</Typography>
                                        {!bundle.isContact && (
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 0.25, opacity: 0.9 }}>/ {t('purchase.per_month', 'month')}</Typography>
                                        )}
                                    </Box>

                                    {/* keep 'Recommended' as the Chip — avoid duplicate per-month captions */}
                                </Box>

                                <Typography variant="caption" color="text.secondary">
                                    {bundle.isContact ? t('purchase.contact_for_pricing', 'Contact us for a quote') : (pricePer ? `${t('purchase.price_per_credit', '≈')} ${pricePer} / ${t('purchase.credit', 'credit')}` : null)}
                                </Typography>

                                <Button
                                    variant={bundle.recommended ? "contained" : "outlined"}
                                    size="large"
                                    fullWidth
                                    disabled={loadingBundle && !isLoading}
                                    onClick={() => bundle.isContact ? window.location.href = 'mailto:contact@dataunfold.com' : handleBuy(bundle.id)}
                                    aria-label={bundle.isContact ? t('purchase.contact_us', 'Contact Us') : t('purchase.top_up', 'Top up')}
                                    sx={{
                                        borderRadius: 2,
                                        mt: 1,
                                        bgcolor: bundle.recommended ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})` : 'transparent',
                                        color: bundle.recommended ? 'white' : bundle.color,
                                        borderColor: bundle.color,
                                        '&:hover': {
                                            filter: 'brightness(1.02)'
                                        }
                                    }}
                                    >
                                    {isLoading ? <CircularProgress size={20} color="inherit" /> : (bundle.isContact ? t('purchase.contact_us', 'Contact Us') : t('purchase.top_up', 'Top up'))}
                                </Button>

                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                    {t('purchase.secure_payment', 'Secure payment. You will be redirected to complete the purchase.')}
                                </Typography>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    {t('purchase.note', 'Questions? Contact support or check our pricing docs.')}
                </Typography>
            </Stack>

            {/* Payment Result Dialog */}
            <Dialog 
                open={dialogOpen} 
                onClose={handleCloseDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        textAlign: 'center',
                        p: 1,
                        boxShadow: '0 24px 48px rgba(0,0,0,0.1)'
                    }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 3, pb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDialog}
                        sx={{
                            position: 'absolute',
                            right: 12,
                            top: 12,
                            color: (theme) => theme.palette.grey[400],
                            '&:hover': { color: (theme) => theme.palette.grey[600] }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    
                    {dialogStatus === 'success' && (
                        <Box sx={{ 
                            width: 80, 
                            height: 80, 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            bgcolor: 'success.light',
                            color: 'success.contrastText',
                            mb: 2,
                            boxShadow: '0 8px 16px rgba(76, 175, 80, 0.2)'
                        }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 48 }} />
                        </Box>
                    )}
                    {dialogStatus === 'cancel' && (
                        <Box sx={{ 
                            width: 80, 
                            height: 80, 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            bgcolor: 'warning.light',
                            color: 'warning.contrastText',
                            mb: 2,
                            boxShadow: '0 8px 16px rgba(255, 152, 0, 0.2)'
                        }}>
                            <CancelOutlinedIcon sx={{ fontSize: 48 }} />
                        </Box>
                    )}
                    {dialogStatus === 'error' && (
                        <Box sx={{ 
                            width: 80, 
                            height: 80, 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            bgcolor: 'error.light',
                            color: 'error.contrastText',
                            mb: 2,
                            boxShadow: '0 8px 16px rgba(244, 67, 54, 0.2)'
                        }}>
                            <ErrorOutlineIcon sx={{ fontSize: 48 }} />
                        </Box>
                    )}
                    
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        {dialogStatus === 'success' ? t('purchase.dialog_success_title', 'Payment Success!') : 
                         dialogStatus === 'cancel' ? t('purchase.dialog_cancel_title', 'Payment Cancelled') : 
                         t('purchase.dialog_error_title', 'Payment Error')}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ px: 4 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {dialogMessage}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 4, pt: 1 }}>
                    <Button 
                        onClick={handleCloseDialog} 
                        variant="contained"
                        size="large"
                        sx={{ 
                            px: 6, 
                            borderRadius: 2, 
                            fontWeight: 700,
                            textTransform: 'none',
                            boxShadow: 'none',
                            bgcolor: dialogStatus === 'success' ? 'success.main' : dialogStatus === 'cancel' ? 'warning.main' : 'error.main',
                            '&:hover': {
                                bgcolor: dialogStatus === 'success' ? 'success.dark' : dialogStatus === 'cancel' ? 'warning.dark' : 'error.dark',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }
                        }}
                    >
                        {t('common.close', 'Close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default PurchaseCredits;
